/**
 * @voltxampere/web — Sprint 13 canvas shortcut catalog.
 *
 * Sprint 11 already registers the 20 sembol arm-shortcuts (R/C/
 * L/D/…) and the modal-close Escape into the shared
 * `ShortcutManager`; Sprint 13 takes the `armedSymbol` signal
 * those handlers set and activates the StampTool inside
 * `<vxa-canvas>`.
 *
 * This catalog adds the remaining Sprint 13 keybindings — tool
 * toggles (W / E), undo/redo, clipboard (Cmd+C/V/X), and bulk
 * delete (Delete / Backspace). Local canvas concerns (Tab posture
 * toggle, Space pan, 0 viewport reset, Escape tool cancel) keep
 * living on `<vxa-canvas>`'s own keydown handler so we avoid
 * fighting the modal-Escape registration Sprint 11 owns.
 *
 * Handlers route through a module-level dispatcher table the
 * canvas element fills in on mount; this keeps the catalog
 * decoupled from the (singleton) canvas instance and lets tests
 * stub the handlers cleanly.
 *
 * Sprint 11.0.1 composedPath guard fires *inside* the
 * `ShortcutManager` — the canvas does not need to defend on its
 * own again.
 *
 * ⚠️ Sprint 13 deviation from prompt §3.1 Karar 6: we keep the
 * existing `global` / `modal` scope state machine instead of
 * adding a `canvas` scope. Sprint 11's `Scope` already lists
 * `"canvas"` in the union so the API is reserved, but Sprint 13
 * does not change the active-scope toggle — `global` is the
 * effective canvas scope today (modal blocks it, editable target
 * blocks it, nothing else does). See SPRINT_13_REPORT.md D1.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { shortcuts } from "./index.js";
import { t } from "../i18n/index.js";
import {
  openImportDialog,
  openExportDialog,
  exportCurrentCircuit,
  copySpiceToClipboard,
} from "../store/import-export.js";
import { setPane } from "../store/router.js";

export interface CanvasShortcutHandlers {
  activateWireTool(): void;
  activateEraseTool(): void;
  activateAnnotationTool(): void;
  undo(): void;
  redo(): void;
  copy(): void;
  paste(): void;
  cut(): void;
  deleteSelected(): void;
  /** Sprint 18 Paket C C3 — rotate the current selection 90° CW. */
  rotateSelected(): void;
  runDc(): void;
  /** Sprint 15 — single-shot transient run (F5). */
  runTransient(): void;
  /** Sprint 15 — toggle Live mode (Shift+F5). */
  toggleLive(): void;
  /** Sprint 15 — collapse / expand the waveform panel (F4). */
  toggleWaveform(): void;
  /** Sprint 20 — single-shot AC sweep (F6). */
  runAC(): void;
}

let handlers: CanvasShortcutHandlers | null = null;
let fallbackHandlers: Partial<CanvasShortcutHandlers> | null = null;

/**
 * `<vxa-canvas>` calls this on `firstUpdated`. Tests may swap a
 * stub in to assert dispatch routing without spinning up the
 * full canvas.
 */
export function setCanvasShortcutHandlers(h: CanvasShortcutHandlers | null): void {
  handlers = h;
  if (import.meta.env.DEV) {
    console.info(`[VXA shortcuts] setCanvasShortcutHandlers · ${h === null ? "cleared" : "set"}`);
  }
}

/** Sprint 19 hotfix-4 — pane-independent fallback handlers. F5 /
 *  Shift+F5 keep working even when `<vxa-canvas>` is unmounted by
 *  the pane router (Sprint 18 Paket B sometimes detaches the
 *  simulator pane between hash transitions). main.ts wires the
 *  fallback at boot so the engine pipeline keeps responding to
 *  the shortcut regardless of which pane is currently active. */
export function setCanvasShortcutFallback(f: Partial<CanvasShortcutHandlers> | null): void {
  fallbackHandlers = f;
  if (import.meta.env.DEV) {
    console.info(`[VXA shortcuts] setCanvasShortcutFallback · ${f === null ? "cleared" : "set"}`);
  }
}

/** Sprint 18 Paket C C3 — read the current handler table. The
 *  inspector's action-row buttons reuse the canvas's existing
 *  copy / rotate / delete dispatches; resolving the handlers at
 *  click time keeps that path working across canvas remounts. */
export function getCanvasShortcutHandlers(): CanvasShortcutHandlers | null {
  return handlers;
}

const dispatch = (slot: keyof CanvasShortcutHandlers): (() => void) => {
  return () => {
    if (handlers) {
      if (import.meta.env.DEV) {
        console.info(`[VXA shortcuts] dispatch ${slot} → canvas handler`);
      }
      handlers[slot]();
      return;
    }
    const fb = fallbackHandlers?.[slot];
    if (fb) {
      if (import.meta.env.DEV) {
        console.info(`[VXA shortcuts] dispatch ${slot} → fallback (canvas unmounted)`);
      }
      fb();
      return;
    }
    if (import.meta.env.DEV) {
      console.warn(`[VXA shortcuts] dispatch ${slot} · NO HANDLER (canvas + fallback both null)`);
    }
  };
};

export function registerCanvasShortcuts(): void {
  // Sprint 17 hotfix: `W` is intentionally NOT registered here.
  // `registerBuiltinShortcuts` (catalog.ts) iterates ALL_SYMBOLS
  // and the `wire` symbol's `shortcut: "W"` already binds W to
  // arming `armedSymbol = wire`. Re-binding here would have made
  // the canvas-catalog override the symbol-arm path (last-wins
  // in the ShortcutManager). The pre-hotfix code kept BOTH
  // registrations and emitted a startup warning every boot.
  // Sprint 18+ will move WireTool activation onto the
  // `armedSymbol` effect inside `<vxa-canvas>` so the symbol-arm
  // path stays the single source of truth.
  shortcuts.register({
    key: "E",
    scope: "global",
    handler: dispatch("activateEraseTool"),
    describe: () => "Erase mode",
  });
  // Undo / redo (cross-platform Cmd/Ctrl unified by ShortcutManager).
  shortcuts.register({
    key: "Cmd+Z",
    scope: "any",
    handler: dispatch("undo"),
    describe: () => "Undo",
  });
  shortcuts.register({
    key: "Cmd+Shift+Z",
    scope: "any",
    handler: dispatch("redo"),
    describe: () => "Redo",
  });
  shortcuts.register({
    key: "Cmd+C",
    scope: "global",
    handler: dispatch("copy"),
    describe: () => "Copy",
  });
  shortcuts.register({
    key: "Cmd+V",
    scope: "global",
    handler: dispatch("paste"),
    describe: () => "Paste",
  });
  shortcuts.register({
    key: "Cmd+X",
    scope: "global",
    handler: dispatch("cut"),
    describe: () => "Cut",
  });
  shortcuts.register({
    key: "Delete",
    scope: "global",
    handler: dispatch("deleteSelected"),
    describe: () => t("shortcut.delete.describe"),
  });
  shortcuts.register({
    key: "Backspace",
    scope: "global",
    handler: dispatch("deleteSelected"),
    describe: () => t("shortcut.delete.describe"),
  });
  // Sprint 18 Paket C C3 — Shift+R rotates the current selection.
  // Bare `R` is already taken by the resistor symbol-arm; using
  // Shift+R keeps the symbol-arm path the single source of truth.
  shortcuts.register({
    key: "Shift+R",
    scope: "global",
    handler: dispatch("rotateSelected"),
    describe: () => "Seçimi döndür",
  });
  // Sprint 14
  shortcuts.register({
    key: "T",
    scope: "global",
    handler: dispatch("activateAnnotationTool"),
    describe: () => "Annotation modu",
  });
  // Sprint 15 — F5 rebound to transient run-once. DC moves to
  // Cmd+Shift+D (the inspector "Run DC" button keeps firing the
  // same handler, just under a new keyboard binding).
  shortcuts.register({
    key: "F5",
    scope: "any",
    handler: dispatch("runTransient"),
    describe: () => "Transient çalıştır (run-once)",
  });
  shortcuts.register({
    key: "Shift+F5",
    scope: "any",
    handler: dispatch("toggleLive"),
    describe: () => "Transient live mode (toggle)",
  });
  shortcuts.register({
    key: "Cmd+Shift+D",
    scope: "any",
    handler: dispatch("runDc"),
    describe: () => "DC çalıştır",
  });
  shortcuts.register({
    key: "F4",
    scope: "any",
    handler: dispatch("toggleWaveform"),
    describe: () => "Waveform paneli (toggle)",
  });
  // Sprint 20 — single-shot AC analysis sweep.
  shortcuts.register({
    key: "F6",
    scope: "any",
    handler: dispatch("runAC"),
    describe: () => "AC analizi çalıştır (run-once)",
  });

  // Sprint 16 — Import / Export
  shortcuts.register({
    key: "Cmd+I",
    scope: "any",
    handler: () => {
      openImportDialog();
    },
    describe: () => "İçe aktar dialog'u",
  });
  shortcuts.register({
    key: "Cmd+E",
    scope: "any",
    handler: () => {
      openExportDialog();
    },
    describe: () => "Dışa aktar dialog'u",
  });
  shortcuts.register({
    key: "Cmd+Shift+E",
    scope: "any",
    handler: () => {
      const payload = exportCurrentCircuit();
      if (payload !== null) void copySpiceToClipboard(payload);
    },
    describe: () => "Panoya hızlı dışa aktar",
  });

  // Sprint 17 — Preset selector via Cmd+Shift+P. Sprint 18 Paket B
  // B8: the shortcut now routes to the DEVRELER nav-tab and the
  // modal preset dialog is deprecated (the page-level
  // <vxa-pane-presets> already provides the full preset list and
  // its filters).
  shortcuts.register({
    key: "Cmd+Shift+P",
    scope: "any",
    handler: () => {
      setPane("presets");
    },
    describe: () => "Hazır devrelere geç",
  });
}
